import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Switch from '/@/components/switch';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { _ } from '/@/locale';
import {
    setCatalogCropType as setCatalogCropTypeAction,
    setCatalogProductType as setCatalogProductTypeAction,
    setCatalogSortField as setCatalogSortFieldAction,
    setCropProtectionTargetSearch as setCropProtectionTargetSearchAction,
} from '/@/store/catalog/actions';
import {
    getCatalogCropType,
    getCatalogProductType,
    getCatalogSortField,
    getCropProtectionTargetSearch,
} from '/@/store/catalog/selectors';
import { getSessionLanguage } from '/@/store/session/selectors';
import Input from '/@/v3/components/forms/input';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const CropProtectionFilters = ({ options }) => {
    const dispatch = useDispatch();

    const [showAllFilters, setAllMoreFilters] = useState(false);
    const cropType = useSelector(getCatalogCropType);
    const productType = useSelector(getCatalogProductType);
    const language = useSelector(getSessionLanguage);
    const target = useSelector(getCropProtectionTargetSearch);
    const sortField = useSelector(getCatalogSortField);

    const [localTarget, setLocalTarget] = useState(target || '');
    const debouncedTarget = useDebounce(localTarget, 500);

    const setCatalogCropType = bindActionCreators(setCatalogCropTypeAction, dispatch);
    const setCatalogProductType = bindActionCreators(setCatalogProductTypeAction, dispatch);
    const setCropProtectionTargetSearch = bindActionCreators(setCropProtectionTargetSearchAction, dispatch);
    const setCatalogSortField = bindActionCreators(setCatalogSortFieldAction, dispatch);

    const handleCatalogCropTypeChange = (e) => {
        setCatalogCropType(e);
        setCatalogProductType(undefined);
        setCropProtectionTargetSearch('');
        setLocalTarget('');
    };

    const cleanFilters = () => {
        setCatalogCropType(undefined);
        setCatalogProductType(undefined);
        setCropProtectionTargetSearch('');
        setLocalTarget('');
    };

    const sortOptions = [
        {
            value: 'brandName',
            label: _('Nombre del producto'),
        },
        {
            value: `cropName_${language}`,
            label: _('Cultivo'),
        },
        {
            value: `typeName_${language}`,
            label: _('Tipo'),
        },
    ];

    const filters = [
        {
            title: _('Ordena alfabéticamente por'),
            value: sortField,
            options: sortOptions,
            onChange: setCatalogSortField,
            placeholder: _('Campos'),
            disabled: false,
        },
        {
            title: language === 'pt' ? 'Cultura' : _('Cultivo'),
            value: cropType,
            options: options.crops
                ? options.crops.map((o) => {
                      return { label: _(o), value: o?.en?.toLowerCase() };
                  })
                : [],
            onChange: handleCatalogCropTypeChange,
            placeholder: _('Cultivos'),
            disabled: showAllFilters,
        },
        {
            title: _('Tipo de producto'),
            value: productType,
            options: options.products
                ? options.products.map((o) => {
                      return { label: _(o), value: o?.en?.toLowerCase() };
                  })
                : [],
            onChange: setCatalogProductType,
            placeholder: _('Productos'),
            disabled: showAllFilters,
        },
        {
            title: _('Target'),
            value: localTarget, // Use the local state here
            onChange: (e) => setLocalTarget(e), // Update the local state on change
            placeholder: _('Enfermedades, Plagas y Malezas'),
            extraClass: styles.select,
            disabled: showAllFilters,
        },
    ];

    // Update the Redux store when the debounced value changes
    useEffect(() => {
        setCropProtectionTargetSearch(debouncedTarget);
    }, [debouncedTarget]);

    return (
        <Grid container item>
            <Grid
                container
                item
                justifyContent='center'
                alignItems='center'
                sx={{ p: 1, borderBottom: 1, borderColor: colors.gray[400] }}
                checked={showAllFilters}
                onChange={(e) => {
                    cleanFilters();
                    setAllMoreFilters(e.target.checked);
                }}
            >
                <Grid item xs={10} sx={{ p: 1 }}>
                    <Title className={styles.label} size='sm' bolder theme={settings.app.theme}>
                        {_('Mostrar todo')}
                    </Title>
                </Grid>
                <Grid item container xs={2} justifyContent='flex-end'>
                    <Switch sx={{ m: 1 }} />
                </Grid>
            </Grid>

            {Object.values(options).length ? (
                filters.map((filter, i) => (
                    <Grid container item sx={{ p: 1, borderBottom: 1, borderColor: colors.gray[400] }} key={i}>
                        <Grid item xs={12} sx={{ px: 1, pb: 1 }}>
                            <Title
                                size='sm'
                                bolder
                                theme={filter.disabled ? undefined : settings.app.theme}
                                className={classNames(styles.label, { [styles.disabledText]: filter.disabled })}
                            >
                                {filter.title}
                            </Title>
                        </Grid>
                        <Grid item xs={12}>
                            {filter.options ? (
                                <Select
                                    color={settings.app.theme}
                                    appearance='outline'
                                    value={filter.value}
                                    options={filter.options}
                                    onChange={filter.onChange}
                                    placeholder={filter.placeholder}
                                    className={styles.select}
                                    disabled={filter.disabled}
                                />
                            ) : (
                                <Input
                                    color={settings.app.theme}
                                    appearance='outline'
                                    value={filter.value}
                                    onChange={(e) => {
                                        filter.onChange(e);
                                    }} // Pass the value to the onChange
                                    placeholder={filter.placeholder}
                                    extraClass={styles.select}
                                    disabled={filter.disabled}
                                />
                            )}
                        </Grid>
                    </Grid>
                ))
            ) : (
                <Skeleton variant='rectangular' width='100%' height={350} />
            )}
        </Grid>
    );
};

export default CropProtectionFilters;
